import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Scale, Lock } from 'lucide-react';
import * as tienda from '@/api/tienda.api';
import type { Paca, Reparto, Suplidor } from '@/api/tienda.api';
import { DataTable, type Columna } from '@/components/datos/DataTable';
import { ConDetalle } from '@/components/datos/DetailDrawer';
import { EntityLink } from '@/components/datos/EntityLink';
import { useAbrirDetalle, type Entidad } from '@/components/datos/contexto-detalle';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { VistasGuardadas } from '@/components/datos/VistasGuardadas';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { useToast } from '@/hooks/useToast';
import { dinero, numero, fecha } from '@/lib/formato-tienda';
import { DetalleDePaca, DetalleDeProducto, DetalleDeVenta } from '@/components/datos/detalles';

/**
 * PACAS — de dónde salió la mercancía y cuánto costó cada pieza.
 *
 * ═══ EL PROBLEMA QUE RESUELVE ═══
 *
 * Maciel compra un bulto por un precio: «la paca de octubre, RD$28,000». Lo
 * que vende son piezas sueltas. Entre esas dos cosas hay una cuenta que hoy
 * hace de cabeza, y mientras no la haga, TODO el margen del sistema es mentira
 * — porque las piezas entran sin costo.
 *
 * ═══ POR QUÉ EL REPARTO SON DOS PASOS ═══
 *
 * Calcular no escribe nada. Aplicar escribe el costo de cada pieza y CIERRA la
 * paca. Son dos botones distintos a propósito: repartir mal el costo de 60
 * piezas no se deshace con «ctrl+z», y el número que sale de ahí es el que
 * decide precios durante meses. Primero se ve, después se firma.
 *
 * El reparto por defecto es parejo. El peso existe porque un abrigo no costó
 * lo mismo que una camiseta aunque vinieran en el mismo bulto: peso 2 = el
 * doble de la porción que le toca a un peso 1.
 */

const VISTAS = [
  { id: 'abiertas', etiqueta: 'Abiertas', ayuda: 'Falta repartir su costo' },
  { id: 'cerradas', etiqueta: 'Cerradas' },
  { id: 'todas', etiqueta: 'Todas' },
] as const;

export default function Pacas() {
  return (
    <ConDetalle render={(e) => <Detalle entidad={e} />}>
      <Contenido />
    </ConDetalle>
  );
}

function Detalle({ entidad }: { entidad: Entidad }) {
  if (entidad.tipo === 'paca') return <DetalleDePaca id={entidad.id} />;
  if (entidad.tipo === 'producto') return <DetalleDeProducto id={entidad.id} />;
  if (entidad.tipo === 'venta') return <DetalleDeVenta id={entidad.id} />;
  return <p className="text-sm text-slate-500">Nada que mostrar.</p>;
}

function Contenido() {
  const abrir = useAbrirDetalle();
  const { filtros, poner, limpiar, activos, hayFiltros } = useFiltrosEnUrl(
    { vista: 'abiertas', buscar: '', nueva: '', repartir: '' },
    { vista: VISTAS.map((v) => v.id) },
  );
  const [suplidores, setSuplidores] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['tienda', 'pacas'],
    queryFn: () => tienda.listarPacas(),
  });
  const pacas = useMemo(() => data ?? [], [data]);

  const abiertas = pacas.filter((p) => !p.cerradaAt);
  const invertido = pacas.reduce((a, p) => a + p.costoTotal, 0);
  const sinRepartir = abiertas.reduce((a, p) => a + p.costoTotal, 0);

  const filtradas = useMemo(() => {
    let salida = pacas;
    if (filtros.vista === 'abiertas') salida = salida.filter((p) => !p.cerradaAt);
    if (filtros.vista === 'cerradas') salida = salida.filter((p) => !!p.cerradaAt);

    const q = filtros.buscar.trim().toLowerCase();
    if (q) {
      salida = salida.filter((p) =>
        `${p.nombre} ${p.suplidor?.nombre ?? ''} ${p.notas ?? ''}`.toLowerCase().includes(q),
      );
    }
    return salida;
  }, [pacas, filtros.vista, filtros.buscar]);

  const columnas: Columna<Paca>[] = [
    {
      id: 'nombre',
      titulo: 'Paca',
      fija: true,
      valor: (p) => p.nombre,
      celda: (p) => (
        <EntityLink
          tipo="paca"
          id={p.id}
          etiqueta={p.nombre}
          detalle={p.suplidor?.nombre ?? undefined}
        />
      ),
    },
    {
      id: 'suplidor',
      titulo: 'Suplidor',
      valor: (p) => p.suplidor?.nombre ?? '',
      celda: (p) => (
        <span className="text-slate-600 dark:text-slate-300">{p.suplidor?.nombre ?? '—'}</span>
      ),
    },
    {
      id: 'fecha',
      titulo: 'Llegó',
      valor: (p) => p.fecha,
      celda: (p) => <span className="text-slate-600 dark:text-slate-300">{fecha(p.fecha)}</span>,
    },
    {
      id: 'costo',
      titulo: 'Costó',
      numerica: true,
      valor: (p) => p.costoTotal,
      celda: (p) => dinero(p.costoTotal),
      total: (fs) => dinero(fs.reduce((a, p) => a + p.costoTotal, 0)),
    },
    {
      id: 'productos',
      titulo: 'Productos',
      numerica: true,
      valor: (p) => p._count?.productos ?? 0,
      celda: (p) => {
        const n = p._count?.productos ?? 0;
        return n === 0 ? (
          <span className="text-amber-600 dark:text-amber-500">ninguno</span>
        ) : (
          <span className="tabular-nums text-slate-900 dark:text-slate-100">{n}</span>
        );
      },
    },
    {
      id: 'declaradas',
      titulo: 'Piezas declaradas',
      numerica: true,
      ocultaPorDefecto: true,
      valor: (p) => p.piezasDeclaradas ?? 0,
      celda: (p) =>
        p.piezasDeclaradas == null ? (
          <span className="text-slate-400">—</span>
        ) : (
          <span className="tabular-nums">{p.piezasDeclaradas}</span>
        ),
    },
    {
      id: 'estado',
      titulo: 'Estado',
      valor: (p) => (p.cerradaAt ? 'cerrada' : 'abierta'),
      celda: (p) =>
        p.cerradaAt ? (
          <Badge variant="success" title={`Cerrada el ${fecha(p.cerradaAt)}`}>
            Costo repartido
          </Badge>
        ) : (
          <Badge variant="warning">Falta repartir</Badge>
        ),
    },
  ];

  return (
    <div className="space-y-5">
      <ModuleHeader
        titulo="Pacas"
        subtitulo="Lo que compraste por bulto. Mientras no repartas su costo, el margen de esas piezas no existe."
        acciones={
          <>
            <Button variant="outline" onClick={() => setSuplidores(true)}>
              Suplidores
            </Button>
            <Button onClick={() => poner('nueva', '1')}>
              <Plus className="h-4 w-4" />
              Nueva paca
            </Button>
          </>
        }
        kpis={
          <>
            <Kpi titulo="Pacas" valor={numero(pacas.length)} onClick={() => poner('vista', 'todas')} />
            <Kpi titulo="Invertido" valor={dinero(invertido)} pie="en todas las pacas" />
            <Kpi
              titulo="Sin repartir"
              valor={dinero(sinRepartir)}
              pie={`${abiertas.length} paca${abiertas.length === 1 ? '' : 's'} abierta${
                abiertas.length === 1 ? '' : 's'
              }`}
              tono={abiertas.length > 0 ? 'malo' : 'normal'}
              bajarEsBueno
              onClick={() => poner('vista', 'abiertas')}
            />
            <Kpi
              titulo="Productos ligados"
              valor={numero(pacas.reduce((a, p) => a + (p._count?.productos ?? 0), 0))}
              pie="piezas que saben de dónde vienen"
            />
          </>
        }
      />

      <VistasGuardadas
        vistas={VISTAS.map((v) => ({
          ...v,
          cuenta:
            v.id === 'abiertas'
              ? abiertas.length
              : v.id === 'cerradas'
                ? pacas.length - abiertas.length
                : pacas.length,
        }))}
        activa={filtros.vista}
        onCambiar={(v) => poner('vista', v)}
      />

      <DataTable
        filas={filtradas}
        columnas={columnas}
        idDe={(p) => p.id}
        cargando={isLoading}
        error={isError ? (error instanceof Error ? error.message : 'Error') : null}
        onReintentar={() => void refetch()}
        buscar={filtros.buscar}
        onBuscar={(v) => poner('buscar', v)}
        placeholderBuscar="Busca por paca o suplidor…"
        filtrosActivos={activos
          .filter((a) => a.clave !== 'nueva' && a.clave !== 'repartir')
          .map((a) => ({
            clave: a.clave,
            etiqueta: `${a.clave}: ${a.valor}`,
            onQuitar: () => poner(a.clave as 'vista' | 'buscar', undefined),
          }))}
        onLimpiarFiltros={hayFiltros ? limpiar : undefined}
        memoria="pacas"
        exportarComo="pacas"
        onAbrirFila={(p) => abrir?.({ tipo: 'paca', id: p.id, etiqueta: p.nombre })}
        accionesDeFila={[
          {
            etiqueta: 'Repartir el costo',
            icono: <Scale className="h-3.5 w-3.5" />,
            ocultaSi: (p) => !!p.cerradaAt,
            onClick: (p) => poner('repartir', p.id),
          },
        ]}
        vacio={{
          titulo: filtros.buscar ? `Nada con "${filtros.buscar}"` : 'Todavía no hay pacas',
          descripcion:
            'Carga la primera con lo que te costó el bulto. Después le dices qué productos vinieron dentro y el sistema calcula cuánto costó cada pieza.',
          accion: <Button onClick={() => poner('nueva', '1')}>Nueva paca</Button>,
        }}
      />

      {filtros.nueva === '1' && <NuevaPaca onCerrar={() => poner('nueva', undefined)} />}
      {filtros.repartir && (
        <RepartirCosto pacaId={filtros.repartir} onCerrar={() => poner('repartir', undefined)} />
      )}
      {suplidores && <Suplidores onCerrar={() => setSuplidores(false)} />}
    </div>
  );
}

function NuevaPaca({ onCerrar }: { onCerrar: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [nombre, setNombre] = useState('');
  const [suplidorId, setSuplidorId] = useState('');
  const [costoTotal, setCostoTotal] = useState('');
  const [piezas, setPiezas] = useState('');
  const [notas, setNotas] = useState('');

  const suplidores = useQuery({
    queryKey: ['tienda', 'suplidores'],
    queryFn: () => tienda.listarSuplidores(),
  });

  const crear = useMutation({
    mutationFn: () =>
      tienda.crearPaca({
        nombre: nombre.trim(),
        suplidorId: suplidorId || null,
        costoTotal: Number(costoTotal),
        piezasDeclaradas: piezas ? Number(piezas) : null,
        notas: notas.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Paca creada. Ahora ligale sus productos desde Productos.');
      void qc.invalidateQueries({ queryKey: ['tienda'] });
      onCerrar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo crear'),
  });

  return (
    <Modal open onClose={onCerrar} title="Nueva paca">
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Cómo le dices
          </label>
          <Input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Paca de octubre"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Suplidor
            </label>
            <Select value={suplidorId} onChange={(e) => setSuplidorId(e.target.value)}>
              <option value="">Sin suplidor</option>
              {(suplidores.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Qué te costó
            </label>
            <Input
              type="number"
              value={costoTotal}
              onChange={(e) => setCostoTotal(e.target.value)}
              placeholder="28000"
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Piezas declaradas <span className="font-normal text-slate-400">(opcional)</span>
          </label>
          <Input
            type="number"
            value={piezas}
            onChange={(e) => setPiezas(e.target.value)}
            placeholder="60"
          />
          <p className="mt-1 text-xs text-slate-500">
            Las que te dijeron que venían. Si después cargas menos, el reparto te avisa cuántas
            faltan en vez de repartir el costo entre las que sí aparecieron.
          </p>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Notas
          </label>
          <Textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={2} />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            onClick={() => crear.mutate()}
            disabled={!nombre.trim() || costoTotal === '' || crear.isPending}
          >
            {crear.isPending ? 'Creando…' : 'Crear paca'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * EL REPARTO, EN DOS PASOS.
 *
 * Paso 1 (`calcular`) no escribe nada: devuelve qué costo le tocaría a cada
 * pieza con los pesos actuales. Paso 2 (`aplicar`) lo escribe y cierra la paca.
 *
 * El `sobrante` no es un error: repartir RD$28,000 entre 60 piezas da 466 con
 * resto. Se muestra porque un peso que desaparece sin explicación es lo que
 * hace desconfiar de todo el número.
 */
function RepartirCosto({ pacaId, onCerrar }: { pacaId: string; onCerrar: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [pesos, setPesos] = useState<Record<string, number>>({});
  const [vistaPrevia, setVistaPrevia] = useState<Reparto | null>(null);

  const paca = useQuery({
    queryKey: ['tienda', 'paca', pacaId],
    queryFn: () => tienda.leerPaca(pacaId),
  });

  const calcular = useMutation({
    mutationFn: () => tienda.calcularReparto(pacaId, Object.keys(pesos).length ? pesos : undefined),
    onSuccess: (r) => setVistaPrevia(r),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo calcular'),
  });

  const aplicar = useMutation({
    mutationFn: () => tienda.aplicarReparto(pacaId, Object.keys(pesos).length ? pesos : undefined),
    onSuccess: () => {
      toast.success('Costo repartido. La paca queda cerrada.');
      void qc.invalidateQueries({ queryKey: ['tienda'] });
      void qc.invalidateQueries({ queryKey: ['productos'] });
      void qc.invalidateQueries({ queryKey: ['inventario'] });
      onCerrar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo aplicar'),
  });

  const r = vistaPrevia;

  // Una sola forma para la tabla, venga del cálculo o de la paca sin calcular.
  // Mezclar las dos formas dentro del `map` es como se cuelan los `undefined`
  // que solo aparecen cuando el usuario todavía no ha pulsado Calcular.
  const filas = useMemo(() => {
    if (r) {
      return r.productos.map((p) => ({
        id: p.productoId,
        nombre: p.nombre,
        piezas: p.piezas as number | null,
        costoPorPieza: p.costoPorPieza as number | null,
        costoActual: p.costoActual,
      }));
    }
    return (paca.data?.productos ?? []).map((p) => ({
      id: p.id,
      nombre: p.name,
      piezas: null,
      costoPorPieza: null,
      costoActual: p.cost,
    }));
  }, [r, paca.data]);

  return (
    <Modal open onClose={onCerrar} title={`Repartir el costo · ${paca.data?.nombre ?? ''}`}>
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          La paca costó <strong>{dinero(paca.data?.costoTotal)}</strong>. Ese costo se reparte entre
          las piezas que trajo, y a partir de ahí cada venta sabe cuánto dejó.
        </p>

        {paca.data && paca.data.productos.length === 0 ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
            Esta paca no tiene productos ligados todavía. Ve a Productos, abre cada uno y dile que
            vino de esta paca. Sin eso no hay entre qué repartir.
          </div>
        ) : (
          <>
            <div className="max-h-80 overflow-auto rounded-lg border border-surface-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-surface-muted text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Producto</th>
                    <th className="px-3 py-2 text-right">Piezas</th>
                    <th className="px-3 py-2 text-right">Peso</th>
                    <th className="px-3 py-2 text-right">Costo por pieza</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {filas.map((f) => (
                    <tr key={f.id}>
                      <td className="px-3 py-2">
                        <span className="text-slate-900 dark:text-slate-100">{f.nombre}</span>
                        {f.costoActual != null && (
                          <span className="ml-2 text-xs text-slate-400">
                            hoy {dinero(f.costoActual)}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600 dark:text-slate-300">
                        {f.piezas ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          value={pesos[f.id] ?? 1}
                          onChange={(e) =>
                            setPesos((v) => ({ ...v, [f.id]: Number(e.target.value) }))
                          }
                          className="ml-auto h-8 w-20 text-right"
                        />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-900 dark:text-slate-100">
                        {f.costoPorPieza == null ? '—' : dinero(f.costoPorPieza)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-slate-500">
              Peso 1 = le toca la porción normal. Peso 2 = el doble que a una de peso 1. Si no
              tocas nada, se reparte parejo.
            </p>

            {r && (
              <div className="space-y-1 rounded-lg border border-surface-border bg-surface-muted px-4 py-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Piezas entre las que se reparte</span>
                  <span className="tabular-nums">{numero(r.piezasTotales)}</span>
                </div>
                {r.faltanPiezas != null && r.faltanPiezas > 0 && (
                  <div className="flex justify-between text-amber-700 dark:text-amber-500">
                    <span>Faltan por cargar</span>
                    <span className="tabular-nums">{numero(r.faltanPiezas)} piezas</span>
                  </div>
                )}
                {r.sobrante > 0 && (
                  <div className="flex justify-between text-slate-500">
                    <span>Sobrante del redondeo</span>
                    <span className="tabular-nums">{dinero(r.sobrante)}</span>
                  </div>
                )}
              </div>
            )}

            {r && r.faltanPiezas != null && r.faltanPiezas > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                Declaraste más piezas de las que hay cargadas. Si aplicas así, el costo de las que
                faltan se reparte entre las que sí están y cada una va a salir más cara de lo que
                fue.
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-2 border-t border-surface-border pt-3">
              <Button variant="ghost" onClick={onCerrar}>
                Cancelar
              </Button>
              <Button
                variant="outline"
                onClick={() => calcular.mutate()}
                disabled={calcular.isPending}
              >
                {calcular.isPending ? 'Calculando…' : r ? 'Recalcular' : 'Calcular'}
              </Button>
              <Button onClick={() => aplicar.mutate()} disabled={!r || aplicar.isPending}>
                <Lock className="h-4 w-4" />
                {aplicar.isPending ? 'Aplicando…' : 'Aplicar y cerrar la paca'}
              </Button>
            </div>
            {!r && (
              <p className="text-right text-xs text-slate-500">
                Primero calcula para ver qué va a pasar. Aplicar escribe el costo y cierra la paca.
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function Suplidores({ onCerrar }: { onCerrar: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');

  const { data } = useQuery({
    queryKey: ['tienda', 'suplidores'],
    queryFn: () => tienda.listarSuplidores(),
  });

  const crear = useMutation({
    mutationFn: () =>
      tienda.crearSuplidor({ nombre: nombre.trim(), telefono: telefono.trim() || null }),
    onSuccess: () => {
      toast.success('Suplidor agregado');
      setNombre('');
      setTelefono('');
      void qc.invalidateQueries({ queryKey: ['tienda', 'suplidores'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo agregar'),
  });

  return (
    <Modal open onClose={onCerrar} title="Suplidores">
      <div className="space-y-4">
        <p className="text-sm text-slate-500">
          A quién le compras. Ligar la paca a su suplidor es lo que después responde «¿a quién me
          conviene volver a comprarle?».
        </p>

        <ul className="divide-y divide-surface-border rounded-lg border border-surface-border">
          {(data ?? []).length === 0 ? (
            <li className="px-3 py-4 text-center text-sm text-slate-400">
              Todavía no hay suplidores.
            </li>
          ) : (
            (data ?? []).map((s: Suplidor) => (
              <li key={s.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="text-slate-900 dark:text-slate-100">{s.nombre}</span>
                <span className="font-mono text-xs text-slate-500">{s.telefono ?? '—'}</span>
              </li>
            ))
          )}
        </ul>

        <div className="grid grid-cols-[1fr_auto] gap-2">
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Nombre"
            />
            <Input
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="Teléfono"
            />
          </div>
          <Button onClick={() => crear.mutate()} disabled={!nombre.trim() || crear.isPending}>
            <Plus className="h-4 w-4" />
            Agregar
          </Button>
        </div>

        <div className="flex justify-end">
          <Button variant="ghost" onClick={onCerrar}>
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
